import { useAtom, atom } from 'jotai';
import { picstudioDriveDialogOpenAtom, picstudioDriveDialogReturnUrlAtom } from '@/atoms/picstudioDriveDialog';
import { improveResultsDialogOpenAtom, IMPROVE_RESULTS_DIALOG_ID } from '@/atoms/improveResultsDialog';
import {
  UpscaleYourLikedPhotoDialogOpenAtom,
  UPSCALE_YOUR_LIKED_PHOTO_DIALOG_ID
} from '@/atoms/upscaleYourLikedPhotoDialog';
import { usePicStudioDriveSubscription } from '@hooks/usePicStudioDriveSubscription';
import { PicStudioDriveDialog } from './Dialog/PicStudioDriveDialog';
import { ImproveResultsDialog } from './Dialog/ImproveResultsDialog';
import { UpscaleYourLikedPhotoDialog } from './Dialog/UpscaleYourLikedPhotoDialog';
import { api } from '@/trpc/react';
import { useSession } from '@hooks/index';
import { DoOverDialog } from './Dialogs/DoOverDialog';
import { useCookies } from 'react-cookie';
import * as React from 'react';
import { usePathname, useParams } from 'next/navigation';

const DO_OVER_DIALOG_COOKIE = 'doOverDialogDismissed';

// Atom for DoOverDialog
const doOverDialogOpenAtom = atom(false);

// Define a type for our active credit context
type ActiveCreditContext =
  | { type: 'team'; teamId: string; teamName: string; redoCredits: number }
  | { type: 'personal'; redoCredits: number }
  | null;

function PicStudioDriveDialogInternal() {
  const [isDialogOpen, setIsDialogOpen] = useAtom(picstudioDriveDialogOpenAtom);
  const [returnUrl] = useAtom(picstudioDriveDialogReturnUrlAtom);
  const { isSubscribed, isLoading: isSubscriptionLoading } = usePicStudioDriveSubscription();

  if (isSubscribed && !isSubscriptionLoading) {
    return null;
  }

  return (
    <PicStudioDriveDialog
      open={isDialogOpen}
      onOpenChange={setIsDialogOpen}
      returnUrl={returnUrl}
      isLoading={isSubscriptionLoading}
    />
  );
}

function ImproveResultsDialogInternal() {
  const [atom, setAtom] = useAtom(improveResultsDialogOpenAtom);
  const { isLoggedIn } = useSession();

  // Check if user has dismissed this dialog before
  const { data, isLoading } = api.userDismissedItem.hasDismissedItem.useQuery(
    {
      itemId: IMPROVE_RESULTS_DIALOG_ID,
      itemType: 'dialog'
    },
    {
      // Don't show loading states or errors for this query
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isLoggedIn
    }
  );

  if (!isLoggedIn) {
    return null;
  }
  // If we're still loading or user has dismissed, don't show the dialog
  if (isLoading || (data && data.isDismissed)) {
    return null;
  }

  return (
    <ImproveResultsDialog open={atom.open} onOpenChange={open => setAtom({ ...atom, open })} modelId={atom.modelId!} />
  );
}

function UpscaleYourLikedPhotoDialogInternal() {
  const [atom, setAtom] = useAtom(UpscaleYourLikedPhotoDialogOpenAtom);
  const { isLoggedIn } = useSession();

  // Check if user has dismissed this dialog before
  const { data, isLoading } = api.userDismissedItem.hasDismissedItem.useQuery(
    {
      itemId: UPSCALE_YOUR_LIKED_PHOTO_DIALOG_ID,
      itemType: 'dialog'
    },
    {
      // Don't show loading states or errors for this query
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isLoggedIn
    }
  );

  // If we're still loading or user has dismissed, don't show the dialog
  if (isLoading || (data && data.isDismissed) || !atom.image || !atom.albumId) {
    return null;
  }

  return (
    <UpscaleYourLikedPhotoDialog
      open={atom.open}
      onOpenChange={open => setAtom({ ...atom, open })}
      modelId={atom.modelId!}
      image={atom.image}
      albumId={atom.albumId}
    />
  );
}

function DoOverDialogInternal() {
  const [isOpen, setIsOpen] = useAtom(doOverDialogOpenAtom);
  const [cookies, setCookie] = useCookies([DO_OVER_DIALOG_COOKIE]);
  const { isLoggedIn } = useSession();
  const pathname = usePathname();
  const params = useParams();
  const modelRepositoryIdFromPath = params?.modelId as string | undefined;

  const [activeCreditContext, setActiveCreditContext] = React.useState<ActiveCreditContext>(null);
  const [wasManuallyClosed, setWasManuallyClosed] = React.useState(false);

  // 1. Fetch teams where the user has redo credits
  const teamsWithRedoCreditsQuery = api.team.listMyTeamsWithRedoCredits.useQuery(
    undefined, // No input for this query as defined
    {
      enabled: isLoggedIn, // Only fetch if logged in
      refetchOnWindowFocus: false,
      retry: 1 // Retry once if it fails initially
    }
  );

  // 2. Fetch personal redo credits, only if no teams with credits are found or still loading teams
  const personalRedoCreditsQuery = api.userCredits.get.useQuery(
    {}, // Assuming no specific input needed for personal credits
    {
      enabled:
        isLoggedIn &&
        (!teamsWithRedoCreditsQuery.data || teamsWithRedoCreditsQuery.data.length === 0) &&
        !teamsWithRedoCreditsQuery.isLoading,
      refetchOnWindowFocus: false,
      retry: 1
    }
  );

  // 3. Determine activeCreditContext (prioritizing team credits)
  React.useEffect(() => {
    if (!isLoggedIn) {
      setActiveCreditContext(null);
      return;
    }

    if (
      teamsWithRedoCreditsQuery.isSuccess &&
      teamsWithRedoCreditsQuery.data &&
      teamsWithRedoCreditsQuery.data.length > 0
    ) {
      const firstTeamWithCredits = teamsWithRedoCreditsQuery.data[0]; // As per plan, pick the first
      if (firstTeamWithCredits) {
        setActiveCreditContext({
          type: 'team',
          teamId: firstTeamWithCredits.teamId,
          teamName: firstTeamWithCredits.teamName,
          redoCredits: firstTeamWithCredits.redoCredits
        });
      }
    } else if (
      teamsWithRedoCreditsQuery.isSuccess && // Teams query is done, and no teams found
      personalRedoCreditsQuery.isSuccess &&
      personalRedoCreditsQuery.data &&
      personalRedoCreditsQuery.data.redoCredits &&
      personalRedoCreditsQuery.data.redoCredits > 0
    ) {
      setActiveCreditContext({ type: 'personal', redoCredits: personalRedoCreditsQuery.data.redoCredits });
    } else if (!teamsWithRedoCreditsQuery.isLoading && !personalRedoCreditsQuery.isLoading) {
      // Both queries are done, and no credits found
      setActiveCreditContext(null);
    }
    // If queries are loading, context will be set once they resolve.
  }, [
    isLoggedIn,
    teamsWithRedoCreditsQuery.isSuccess,
    teamsWithRedoCreditsQuery.data,
    teamsWithRedoCreditsQuery.isLoading,
    personalRedoCreditsQuery.isSuccess,
    personalRedoCreditsQuery.data,
    personalRedoCreditsQuery.isLoading
  ]);

  const isLoadingCredits = teamsWithRedoCreditsQuery.isLoading || personalRedoCreditsQuery.isLoading;
  const isOnDoOverPage = pathname ? /^\/dashboard\/[^/]+\/do-over$/.test(pathname) : false;
  const shouldShowBasedOnPath = pathname ? pathname.startsWith('/dashboard') && !isOnDoOverPage : false;

  React.useEffect(() => {
    const mainConditionsMet =
      isLoggedIn &&
      !isLoadingCredits &&
      activeCreditContext !== null && // Now depends on the active credit context having credits
      shouldShowBasedOnPath;

    if (!mainConditionsMet) {
      setWasManuallyClosed(false);
    }

    const shouldBeOpen = mainConditionsMet && !wasManuallyClosed && !cookies[DO_OVER_DIALOG_COOKIE];

    if (isOpen !== shouldBeOpen) {
      setIsOpen(shouldBeOpen);
    }
  }, [
    isLoggedIn,
    isLoadingCredits,
    activeCreditContext, // Dependency on the determined credit context
    shouldShowBasedOnPath,
    cookies,
    isOpen,
    wasManuallyClosed,
    setIsOpen
  ]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setWasManuallyClosed(true);
      const expiryDate = new Date();
      expiryDate.setTime(expiryDate.getTime() + 60 * 60 * 1000); // 1 hour
      setCookie(DO_OVER_DIALOG_COOKIE, true, { path: '/', expires: expiryDate });
    }
  };

  // Conditional rendering of the dialog itself
  // The final check for cookie and other conditions before rendering the component
  if (
    !isLoggedIn ||
    isLoadingCredits || // Still loading any credit info
    activeCreditContext === null || // No active context with credits
    cookies[DO_OVER_DIALOG_COOKIE] ||
    !shouldShowBasedOnPath // Updated condition
  ) {
    return null;
  }

  // If we reach here, activeCreditContext is not null
  const dialogMode = activeCreditContext.type;
  const contextualTeamId = dialogMode === 'team' ? activeCreditContext.teamId : undefined;

  return (
    <DoOverDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      // teamId is deprecated here, dialogMode and contextualTeamId are new
      dialogMode={dialogMode}
      contextualTeamId={contextualTeamId}
      defaultModelRepositoryId={modelRepositoryIdFromPath}
    />
  );
}

export function GlobalDialogs() {
  return (
    <>
      <PicStudioDriveDialogInternal />
      <ImproveResultsDialogInternal />
      <UpscaleYourLikedPhotoDialogInternal />
      <DoOverDialogInternal />
    </>
  );
}
